import { z } from "zod";

import { salesNavigatorUrlRefusal } from "@signalist/shared-types";

import { MetaDataSchema } from "@/schemas/meta-data";
import { ERROR_TYPE } from "@/utils";

import { LiSnType } from "@/enum/li-sn.enum";

import { LiSnResultSchema } from "./liSnResult";

const { REQUIRED } = ERROR_TYPE;

export enum IncludedOrExcluded {
	INCLUDED = "INCLUDED",
	UNSURE = "UNSURE",
}

export enum LiSnPreviewError {
	NO_USER_FOUND = "NO_USER_FOUND",
}

export const LiSnFilterSchema = z.object({
	name: z.string(),
	id: z.number().or(z.string()),
	includedOrExcluded: z.nativeEnum(IncludedOrExcluded).optional(),
	image: z.string().optional(),
});

export const LiSnSuggestSchema = z.object({
	data: z.array(
		z.object({
			id: z.string(),
			displayValue: z.string(),
			headline: z.string(),
			headlineV2: z.object({
				text: z.string(),
				attributes: z.array(
					z.object({
						start: z.number(),
						length: z.number(),
						type: z.record(z.string(), z.any()),
						attributeKindUnion: z.record(z.string(), z.any()),
					})
				),
			}),
		})
	),
});

export const LiSnSchema = z
	.object({
		id: z.number(),
		liSnResults: z.array(LiSnResultSchema).optional(),
		name: z.string().min(4, { message: REQUIRED }).trim(),
		currentCompany: z.any().optional(),
		companyHeadcount: z.any().optional(),
		pastCompanyList: z.array(z.any()).optional(),
		companyType: z.any().optional(),
		companyHeadquarterLocation: z.any().optional(),
		function: z.any().optional(),
		currentJobTitle: z.any().optional(),
		seniorityLevel: z.any().optional(),
		pastJobTitle: z.any().optional(),
		yearsInCurrentCompany: z.any().optional(),
		yearsInCurrentPosition: z.any().optional(),
		geography: z.any().optional(),
		industry: z.any().optional(),
		firstName: z.string().optional(),
		lastName: z.string().optional(),
		profileLanguage: z.any().optional(),
		yearsOfExperience: z.any().optional(),
		school: z.any().optional(),
		connectionsOf: z.any().optional(),
		followingYourCompany: z.any().optional(),
		viewedYourProfileRecently: z.any().optional(),
		pastColleague: z.any().optional(),
		sharedExperiences: z.any().optional(),
		changedJob: z.any().optional(),
		postedOnLinkedin: z.any().optional(),
		plannedScrapingUserId: z.number().optional().nullable(),
		type: z.nativeEnum(LiSnType),
		url: z.string().optional(),
	})
	.superRefine(({ type, url }, ctx) => {
		if (type !== LiSnType.SPECIFIC) {
			return;
		}

		if (!url) {
			ctx.addIssue({
				code: "custom",
				path: ["url"],
				message: REQUIRED,
			});

			return;
		}

		const refusal = salesNavigatorUrlRefusal(url);

		if (refusal) {
			ctx.addIssue({
				code: "custom",
				path: ["url"],
				message: refusal,
			});
		}
	});

export const CreateLiSnSchema = z
	.object({
		id: z.number().optional(),
		name: z.string().min(4, { message: REQUIRED }).trim(),
		currentCompany: z.array(LiSnFilterSchema),
		companyHeadcount: z.array(LiSnFilterSchema),
		pastCompanyList: z.array(LiSnFilterSchema).optional(),
		companyType: z.array(LiSnFilterSchema),
		companyHeadquarterLocation: z.array(LiSnFilterSchema),
		function: z.array(LiSnFilterSchema),
		currentJobTitle: z.array(LiSnFilterSchema),
		seniorityLevel: z.array(LiSnFilterSchema),
		pastJobTitle: z.array(LiSnFilterSchema),
		yearsInCurrentCompany: z.array(LiSnFilterSchema),
		yearsInCurrentPosition: z.array(LiSnFilterSchema),
		geography: z.array(LiSnFilterSchema),
		industry: z.array(LiSnFilterSchema),
		firstName: z.string().optional(),
		lastName: z.string().optional(),
		profileLanguage: z.array(LiSnFilterSchema),
		yearsOfExperience: z.array(LiSnFilterSchema),
		school: z.array(LiSnFilterSchema),
		connectionsOf: z.array(LiSnFilterSchema),
		followingYourCompany: z.boolean(),
		viewedYourProfileRecently: z.boolean(),
		pastColleague: z.boolean(),
		sharedExperiences: z.boolean(),
		changedJob: z.boolean(),
		postedOnLinkedin: z.boolean(),
		listId: z.number().optional(),
		plannedScrapingUserId: z.number().optional().nullable(),
		type: z.nativeEnum(LiSnType),
		url: z.string().optional(),
	})
	.superRefine(({ type, url }, ctx) => {
		if (type !== LiSnType.SPECIFIC) {
			return;
		}

		if (!url) {
			ctx.addIssue({
				code: "custom",
				path: ["url"],
				message: REQUIRED,
			});

			return;
		}

		const refusal = salesNavigatorUrlRefusal(url);

		if (refusal) {
			ctx.addIssue({
				code: "custom",
				path: ["url"],
				message: refusal,
			});
		}
	});

export const UpdateLiSnSchema = z.object({
	id: z.number().optional(),
	name: z.string().min(4, { message: REQUIRED }).trim(),
	currentCompany: z.array(LiSnFilterSchema),
	companyHeadcount: z.array(LiSnFilterSchema),
	pastCompanyList: z.array(LiSnFilterSchema).optional(),
	companyType: z.array(LiSnFilterSchema),
	companyHeadquarterLocation: z.array(LiSnFilterSchema),
	function: z.array(LiSnFilterSchema),
	currentJobTitle: z.array(LiSnFilterSchema),
	seniorityLevel: z.array(LiSnFilterSchema),
	pastJobTitle: z.array(LiSnFilterSchema),
	yearsInCurrentCompany: z.array(LiSnFilterSchema),
	yearsInCurrentPosition: z.array(LiSnFilterSchema),
	geography: z.array(LiSnFilterSchema),
	industry: z.array(LiSnFilterSchema),
	firstName: z.string().optional(),
	lastName: z.string().optional(),
	profileLanguage: z.array(LiSnFilterSchema),
	yearsOfExperience: z.array(LiSnFilterSchema),
	school: z.array(LiSnFilterSchema),
	connectionsOf: z.array(LiSnFilterSchema),
	followingYourCompany: z.boolean(),
	viewedYourProfileRecently: z.boolean(),
	pastColleague: z.boolean(),
	sharedExperiences: z.boolean(),
	changedJob: z.boolean(),
	postedOnLinkedin: z.boolean(),
	listId: z.number().optional(),
	plannedScrapingUserId: z.number().optional().nullable(),
});

export const LiSnResponseSchema = z.object({
	data: z.array(LiSnSchema),
	meta: MetaDataSchema,
});

export const LiSnPreviewItemSchema = z.object({
	type: z.string().optional(),
	industry: z.string().nullable().optional(),
	id: z.string(),
	name: z.string(),
	member_urn: z.string().optional(),
	public_identifier: z.string().optional(),
	profile_url: z.string().optional(),
	public_profile_url: z.string().optional(),
	profile_picture_url: z.string().nullable().optional(),
	profile_picture_url_large: z.string().nullable().optional(),
	network_distance: z.string().optional(),
	location: z.string().optional(),
	headline: z.string().optional(),
	keywords_match: z.string().optional(),
	shared_connections_count: z.number().optional(),
});

export const LiSnPreviewResponseSchema = z.object({
	items: z.array(LiSnPreviewItemSchema),
	totalCount: z.number(),
	usedUserId: z.number().nullable().optional(),
	message: z.string().optional(),
	error: z.enum(LiSnPreviewError).optional(),
});

export type LiSn = z.infer<typeof LiSnSchema>;

export type CreateLiSn = z.infer<typeof CreateLiSnSchema>;

export type UpdateLiSn = z.infer<typeof UpdateLiSnSchema>;

export type LiSnResponse = z.infer<typeof LiSnResponseSchema>;
export type LiSnFilter = z.infer<typeof LiSnFilterSchema>;
export type LiSnSuggest = z.infer<typeof LiSnSuggestSchema>;
export type LiSnPreviewItem = z.infer<typeof LiSnPreviewItemSchema>;
export type LiSnPreviewResponse = z.infer<typeof LiSnPreviewResponseSchema>;
